import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { sortBy } from 'lodash';
import { useSelect, useSelectWithParams } from '../../../../../../utils/selector.utils';
import { meetingTemplatesSelectors } from '../../../../../../app/resource/meeting-templates/meeting-templates.selectors';
import { IStep1Props } from './types';
import { meetingsFoldersPageSelectors } from '../../../../../../app/page/page-storage/meeting-folder/meeting-folder.selectors';
import { meetingFolderSelectors } from '../../../../../../app/resource/meeting-folder/meeting-folder.selectors';
import { IMeetingFolder } from '../../../../../../app/resource/meeting-folder/meeting-folder.types';
import { meetingSimpleSelectors } from '../../../../../../app/resource/meeting-simple/meeting-simple.selectors';
import { meetingsSimplePageSelectors } from '../../../../../../app/page/page-storage/meetings-simple/meetings-simple.selectors';
import { authSelectors } from '../../../../../../app/auth/auth.selectors';
import { userSelectors } from '../../../../../../app/resource/user/user.selectors';
import { isMemberPartOfMeeting } from '../../../../../../utils/user.utils';
import MeetingList from './MeetingList';
import MeetingFolders from './MeetingFolders';
import { IMeetingTemplate } from '../../../../../../app/resource/meeting-templates/meeting-templates.types';

const Step1: FunctionComponent<IStep1Props> = ({ onNext }) => {
  const loggedUserId = useSelect(authSelectors.selectLoggedUser) || '';
  const user = useSelectWithParams(userSelectors.selectResourceById, loggedUserId);
  const isLoggedUserAdmin = useSelect(authSelectors.selectUserIsAdmin);
  const teamIds = useSelect(meetingsSimplePageSelectors.selectTeams);
  let meetings = useSelectWithParams(meetingSimpleSelectors.selectResourcesById, teamIds);
  if (user && isLoggedUserAdmin) {
    meetings = meetings.filter((meeting) => {
      let isMeetingMember = false;
      if (meeting) {
        isMeetingMember = isMemberPartOfMeeting(user, meeting);
      }
      return isMeetingMember;
    });
  }

  const meetingTemplates = sortBy(
    useSelect(meetingTemplatesSelectors.selectResourceList),
    'title',
  ) as IMeetingTemplate[];
  const meetingFoldersIds = useSelect(meetingsFoldersPageSelectors.selectMeetingFoldersIds);
  const meetingFolders = sortBy(
    useSelectWithParams(meetingFolderSelectors.selectResourcesById, meetingFoldersIds) as IMeetingFolder[],
    'name',
  );

  const meetingFoldersWithMeetings = meetingFolders.map((folder) => ({
    id: folder.id,
    name: folder.name,
    meetings: meetings.filter((meeting) => folder.meetingIds.includes(meeting?.id || '')),
  }));

  const meetingWithinFolders = useMemo(
    () =>
      meetingFolders.reduce((accumulator: string[], currentValue) => accumulator.concat(currentValue.meetingIds), []),
    [meetingFolders],
  );

  const meetingsWithoutFolders = useMemo(
    () =>
      sortBy(
        meetings.filter((meeting) => {
          if (meeting) {
            return !meetingWithinFolders.includes(meeting.id);
          }
        }),
        'title',
      ),
    [meetings, meetingWithinFolders],
  );

  const handleSelect = useCallback(
    (operation: 'clone' | 'create') => {
      return (sourceMeetingId: string, agendaItems: any) => {
        onNext({ operation, sourceMeetingId, agendaItems });
      };
    },
    [onNext],
  );

  const defaultMeetingTemplate = meetingTemplates.find((meetingTemplate) => meetingTemplate.isDefault);

  return (
    <div className="flex">
      <div className="pr-2 w50" style={{ borderRight: '1px solid #edf2f5' }}>
        <div className="step1-subtitle f-16 p-2">Choose Default Meeting</div>
        <div>
          {defaultMeetingTemplate && (
            <MeetingList
              meetings={[{ ...defaultMeetingTemplate, title: 'Default meeting' }]}
              onSelect={handleSelect('create')}
            />
          )}
        </div>
        <div className="step1-subtitle f-16 p-2">Clone Existing Meeting</div>
        <div>
          <MeetingFolders meetingFolders={meetingFoldersWithMeetings} onSelect={handleSelect('clone')} />
          <MeetingList meetings={meetingsWithoutFolders} onSelect={handleSelect('clone')} />
        </div>
      </div>

      <div className="pl-2 w50">
        <div className="step1-subtitle f-16 p-2">Choose from Meeting Template</div>
        <div>
          <MeetingList meetings={meetingTemplates} onSelect={handleSelect('create')} />
        </div>
      </div>
    </div>
  );
};
export default Step1;
